// The world store: plain TypeScript between the socket, the PixiJS renderer and React.
// The renderer reads it every frame; the socket writes it as frames arrive; React subscribes and
// is told about changes at most 10 times a second. Nothing here re-renders React per frame.
import { PRESETS, type PresetName } from '@wahala/config';
import { getMap, type MapData, type TaskGame, TILE, type Vec } from '@wahala/maps';
import type { ChatChannel, LeaveCost, Look, PublicEvent, ServerFrame, StickerId } from '@wahala/protocol';
import {
  BASE_SPEED,
  GHOST_SPEED_FACTOR,
  type PlayerView,
  stepPlayer,
  TICK_MS,
  viewBrake,
  viewSpeed,
  WIPER_FACTOR,
} from '@wahala/simulation';
import { hustleKit, loadHustle } from '../game/hustleKit';
import { loadRace, raceKit } from '../game/raceKit';
import { S } from '../strings';

export type Lobby = Extract<ServerFrame, { t: 'lobby' }>;
export interface ChatLine {
  from: string;
  name: string;
  channel: ChatChannel;
  text: string;
  at: number;
}
/** What changes the overhead keke: the roof load and the paint finish (slogans don't show from above). */
export const lookKey = (look: Look | undefined): string => `${look?.load ?? ''}|${look?.finish ?? ''}`;

/** A sticker thrown in the current meeting. */
export interface Thrown {
  from: string;
  at: string;
  sticker: StickerId;
  /** Local receive time: the newest ones land with a slap. */
  receivedAt: number;
}
/** A tutorial tip: what to do now, and why. */
export interface Coach {
  step: string;
  title: string;
  text: string;
  /** A tip that is read, not done: it shows a button to carry on. */
  next?: string;
}
export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'done' | 'wahala' | 'danfo';
  until: number;
  /** A callout: a title in the display face and a glyph, for in-game news nobody should miss. */
  title?: string;
  icon?: string;
  /** Callouts only: how much it matters (2 beats 1 beats 0), and when it came on screen. */
  prio?: number;
  shownAt?: number;
}

/** A small pop beside the purse: the little news of your own (a fare paid, a stop done). */
export interface Blip {
  id: number;
  text: string;
  tone: 'done' | 'danfo' | 'wahala';
  until: number;
}

/** A callout waiting its turn: the lane shows one at a time. */
interface PendingCallout {
  c: { title: string; text?: string | undefined; tone: Toast['tone']; icon: string };
  ms: number;
  prio: number;
  at: number;
}

/** A callout stays at least this long once it is up, even when another is waiting. */
const CALLOUT_MIN_MS = 1_800;
/** A callout that waited longer than this is old news: dropped, never shown late. */
const CALLOUT_STALE_MS = 8_000;
const BLIP_MS = 1_700;

interface Snap {
  view: PlayerView;
  /** Local receive time, for interpolation. */
  at: number;
}

interface PendingInput {
  n: number;
  dx: number;
  dy: number;
  /** Race: Brake held. */
  b?: boolean;
}

const INTERP_DELAY_MS = 120;
/** Past the newest snapshot a remote keke carries on along its last step this long, then holds. */
const EXTRAPOLATE_MS = 100;
/** How quickly the estimate of the server's clock follows each snapshot (arrival jitter averages out). */
const CLOCK_EASE = 0.08;
/** About 2 px at play zoom: corrections smaller than this just snap. */
const SNAP_UNITS = 6;
const MAX_SNAPS = 8;

/** The weather under the sky: clear (day), rain or harmattan. ('night' only in states saved before the clock.) */
export type SkyWeather = 'day' | 'night' | 'rain' | 'harmattan';
export type SkyTime = 'day' | 'evening' | 'night';
/** What the road looks like: a weather at a time of day, as `weather@time` (e.g. `rain@evening`). */
export type Sky = `${SkyWeather}@${SkyTime}`;
/** The weather and the time of day in a sky. */
export function skyParts(sky: Sky): [SkyWeather, SkyTime] {
  const [w, t] = sky.split('@') as [SkyWeather, SkyTime];
  // a night from before the clock: a clear sky, dark
  return w === 'night' ? ['day', 'night'] : [w, t ?? 'day'];
}
const HOURS: readonly SkyTime[] = ['day', 'evening', 'night'];
/** A weather turn blends in over its warning; a new time of day takes a 20-second dusk. */
const TURN_MS = 10_000;
const DUSK_MS = 20_000;

export class World {
  you: string | null = null;
  lobby: Lobby | null = null;
  snaps: Snap[] = [];
  chat: ChatLine[] = [];
  /** Members whose chat this phone hides (BE-17's mute: on this phone only, for this park). */
  muted = new Set<string>();
  toasts: Toast[] = [];
  blips: Blip[] = [];
  private pending: PendingCallout[] = [];
  /** What each player in the park wears (public), and its overhead-drawing key. */
  looks = new Map<string, Look>();
  lookKeys = new Map<string, string>();
  /** Stickers I own (only I am told), and those thrown in this meeting. */
  myStickers: StickerId[] = [];
  /** My role tickets and arm switch (only I am told). */
  mine: { tickets: number; armed: boolean; ticketsAllowed: boolean } = {
    tickets: 0,
    armed: false,
    ticketsAllowed: false,
  };
  /** What leaving the round in play would cost me, as the worker last said (null: it has not said). */
  leaveCost: LeaveCost | null = null;
  thrown: Thrown[] = [];
  /**
   * Events for the world's effects (FE-10): the renderer takes them once a frame and empties the
   * list. Capped, so a hidden tab that isn't drawing never piles them up.
   */
  roadEvents: PublicEvent[] = [];
  /** After a round: this phone left the results board for the lobby (Back to park or Play again). */
  /** Set when a match starts, cleared once the role card has been shown. */
  roleReveal = false;
  /** The meeting now open was called on an impounded (clamped) keke, as this phone saw the road. */
  impounded = false;
  /** Last actions answered by the server: actionId -> ok or reason. */
  answers = new Map<string, string | null>();
  /** Stop the player is holding at: started locally after the server accepted task.interact. */
  holding: { taskId: string; label: string; until: number; startedAt: number } | null = null;
  /** A stop's mini-game on screen (wires, swipe, code). `tries` counts answers the worker refused. */
  game: { taskId: string; game: Exclude<TaskGame, 'hold'>; label: string; tries: number } | null = null;

  // prediction (local keke only)
  predicted: Vec | null = null;
  /** My predicted velocity, world units per tick (speed-up and braking, as the server steps it). */
  predictedVel: Vec = { x: 0, y: 0 };
  /** Race: my predicted heading (256 steps) and speed (hundredths of a unit per tick), and the
   *  server time the prediction has reached. */
  predictedHeading = 0;
  private predictedSpd = 0;
  private predictedNow = 0;
  /** Race: Brake held on the HUD. */
  brake = false;
  /** Race: the map direction the chase camera shows up the screen (radians, 0 east), set by the
   *  road each frame; the stick is turned by it, so pushing up drives on. Null off a race. */
  chaseYaw: number | null = null;
  private inputs: PendingInput[] = [];
  private inputN = 0;
  private lastSent = { dx: 0, dy: 0 };
  private visualOffset: Vec = { x: 0, y: 0 };
  /**
   * The prediction moves a tick (50 ms) at a time; the keke is drawn gliding from where it was drawn
   * toward the newest tick, so it moves at the display's rate instead of jumping 20 times a second
   * (the race camera rides on it, so the whole road would judder).
   */
  private prevPredicted: Vec | null = null;
  private prevHeading = 0;
  private stepAt = 0;
  /** Server time minus local time, eased over the snapshots: remote kekes play on the server's clock. */
  private serverOff = Number.NaN;

  private listeners = new Set<() => void>();
  private dirty = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private toastId = 0;

  get view(): PlayerView | null {
    return this.snaps[this.snaps.length - 1]?.view ?? null;
  }

  /** A stake table (Lagos Life fare): coins never show mid-round there. */
  get stakePark(): boolean {
    return this.lobby?.stakeReady !== undefined;
  }

  get preset(): PresetName {
    const p = this.lobby?.preset;
    return p && p in PRESETS ? (p as PresetName) : 'park';
  }

  /** This park's game is a race (on a race ring, no roles). */
  get isRace(): boolean {
    return PRESETS[this.preset].mode === 'race';
  }

  /** This park's game is City Hustle (a shift in the Lagos city, no roles). */
  get isHustle(): boolean {
    return PRESETS[this.preset].mode === 'hustle';
  }

  /**
   * The id of the map this park plays on (known before the race code is in): the game's own, or the
   * ring the park names (a cash run on the Saturday's Track of the Week).
   */
  get mapId(): string {
    return this.lobby?.map ?? PRESETS[this.preset].mapId;
  }

  /** The map this park plays on. A ring is there only once the race code is loaded (raceKit). */
  get map(): MapData {
    try {
      return getMap(this.mapId);
    } catch {
      return getMap('lagos-route');
    }
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /** Coalesce change notifications to at most one per 100 ms. */
  changed(): void {
    this.dirty = true;
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      if (!this.dirty) return;
      this.dirty = false;
      for (const l of this.listeners) l();
    }, 100);
  }

  /** The 3D street is still loading ("The street dey load"): the road guide waits for it. */
  roadWaiting = false;

  /** The tutorial's tip for the step in hand (practice parks only): it stays until the step is done. */
  coach: Coach | null = null;
  setCoach(c: Coach | null): void {
    this.coach = c;
    this.changed();
  }

  toast(text: string, tone: Toast['tone'] = 'info', ms = 3000): void {
    const now = Date.now();
    // the same words already up stay up longer instead of stacking (a flaky network says it once)
    const same = this.toasts.find((t) => !t.title && t.text === text && t.until > now);
    if (same) same.until = Math.max(same.until, now + ms);
    else
      this.toasts = [...this.toasts.filter((t) => t.until > now), { id: ++this.toastId, text, tone, until: now + ms }];
    this.changed();
  }

  /**
   * In-game news as a callout: coloured by meaning, a title and a glyph. The lane shows one at a
   * time: the same news again only keeps it up, more important news (prio 2: you were knocked, an
   * alarm) takes over at once, and the rest wait their turn behind a short look at the one up now.
   */
  callout(
    c: { title: string; text?: string | undefined; tone: Toast['tone']; icon: string },
    ms = 3500,
    prio = 1,
  ): void {
    const now = Date.now();
    const key = (x: { title?: string | undefined; text?: string | undefined }) => `${x.title}|${x.text ?? ''}`;
    const live = this.toasts.find((t) => t.title && t.until > now);
    if (live && key(live) === key(c)) {
      live.until = Math.max(live.until, now + ms);
      this.changed();
      return;
    }
    if (this.pending.some((p) => key(p.c) === key(c))) return;
    if (!live || prio > (live.prio ?? 1)) {
      this.showCallout(c, ms, prio, now);
      this.changed();
      return;
    }
    this.pending = [...this.pending, { c, ms, prio, at: now }]
      .sort((a, b) => b.prio - a.prio || a.at - b.at)
      .slice(0, 3);
    // the one up now gives way after its short look
    live.until = Math.min(live.until, Math.max(now, (live.shownAt ?? now) + CALLOUT_MIN_MS));
    this.changed();
  }

  private showCallout(c: PendingCallout['c'], ms: number, prio: number, now: number): void {
    this.toasts = [
      ...this.toasts.filter((t) => t.until > now && !t.title),
      {
        id: ++this.toastId,
        text: c.text ?? '',
        title: c.title,
        tone: c.tone,
        icon: c.icon,
        until: now + ms,
        prio,
        shownAt: now,
      },
    ];
  }

  /** What the lane shows now; brings the next waiting callout up once the last one is done. */
  liveToasts(now = Date.now()): Toast[] {
    if (!this.toasts.some((t) => t.title && t.until > now)) {
      this.pending = this.pending.filter((p) => now - p.at < CALLOUT_STALE_MS);
      const next = this.pending.shift();
      if (next) this.showCallout(next.c, next.ms, next.prio, now);
    }
    return this.toasts.filter((t) => t.until > now);
  }

  /** A small pop beside the purse, gone in under two seconds; at most three at once. */
  blip(text: string, tone: Blip['tone'] = 'done'): void {
    const now = Date.now();
    this.blips = [
      ...this.blips.filter((b) => b.until > now).slice(-2),
      { id: ++this.toastId, text, tone, until: now + BLIP_MS },
    ];
    this.changed();
  }

  // ------------------------------------------------------------ inbound frames

  apply(f: ServerFrame, onEvents: (events: PublicEvent[]) => void): void {
    switch (f.t) {
      case 'welcome':
        this.you = f.you;
        this.myStickers = f.stickers ?? [];
        break;
      case 'lobby': {
        const wasIn = this.lobby?.phase === 'IN_MATCH';
        this.lobby = f;
        this.looks = new Map(f.members.map((m) => [m.id, m.look ?? {}]));
        this.lookKeys = new Map(f.members.map((m) => [m.id, lookKey(m.look)]));
        // a race park: its code and the ring load while the lobby waits
        if (this.isRace && !raceKit()) void loadRace().then(() => this.changed());
        // a hustle park: its rules and the city load while the lobby waits
        if (this.isHustle && !hustleKit()) void loadHustle().then(() => this.changed());
        if (f.phase === 'IN_MATCH' && !wasIn) {
          this.roleReveal = true;
          this.chat = [];
          this.resetPrediction();
        }
        if (f.phase === 'WAITING') {
          this.pending = [];
          this.blips = [];
          this.snaps = [];
          this.roadEvents = [];
          this.holding = null;
          this.game = null;
          this.predicted = null;
        }
        break;
      }
      case 'snap':
        this.onSnap(f.view);
        break;
      case 'ev':
        for (const e of f.events) if (this.roadEvents.length < 32) this.roadEvents.push(e);
        onEvents(f.events);
        break;
      case 'ack':
        this.answers.set(f.actionId, null);
        break;
      case 'nack':
        if (f.actionId) this.answers.set(f.actionId, f.reason);
        break;
      case 'sticker':
        this.thrown = [
          ...this.thrown.slice(-40),
          { from: f.from, at: f.at, sticker: f.sticker, receivedAt: performance.now() },
        ];
        break;
      case 'you':
        this.mine = { tickets: f.tickets, armed: f.armed, ticketsAllowed: f.ticketsAllowed };
        this.leaveCost = f.leave ?? null;
        break;
      case 'chat':
        this.chat = [
          ...this.chat.slice(-60),
          // a quick-chat phrase comes as an id: the words are this phone's (strings table)
          {
            from: f.from,
            name: f.name,
            channel: f.channel,
            text: f.quick ? (S.quick[f.quick] ?? '') : f.text,
            at: f.at,
          },
        ];
        break;
      default:
        break;
    }
    this.changed();
  }

  /** The server's clock now, as the snapshots have it (the traffic lights run on it). */
  serverNow(at = performance.now()): number {
    return Number.isNaN(this.serverOff) ? (this.view?.now ?? 0) : at + this.serverOff;
  }

  private onSnap(view: PlayerView): void {
    const last = this.view;
    if (last && view.tick < last.tick) return; // late frame
    const at = performance.now();
    this.snaps.push({ view, at });
    const sample = view.now - at;
    this.serverOff =
      Number.isNaN(this.serverOff) || Math.abs(sample - this.serverOff) > 1000
        ? sample
        : this.serverOff + (sample - this.serverOff) * CLOCK_EASE;
    if (this.snaps.length > MAX_SNAPS) this.snaps.shift();
    // stickers belong to one meeting: back on the road, they're gone
    if (view.phase === 'PLAYING' && this.thrown.length) this.thrown = [];
    if (view.phase !== 'PLAYING') {
      this.inputs = [];
      this.predicted = { ...view.you.pos };
      this.prevPredicted = null;
      this.predictedVel = { ...view.you.vel };
      if (view.race) {
        this.predictedHeading = view.race.car.heading;
        this.predictedSpd = view.race.car.spd;
      }
      this.visualOffset = { x: 0, y: 0 };
      if (this.holding) this.holding = null;
      if (this.game) this.game = null;
      return;
    }
    this.reconcile(view);
  }

  // ------------------------------------------------------------ prediction

  private resetPrediction(): void {
    this.inputs = [];
    this.lastSent = { dx: 0, dy: 0 };
    this.predicted = null;
    this.prevPredicted = null;
    this.predictedVel = { x: 0, y: 0 };
    this.visualOffset = { x: 0, y: 0 };
  }

  private speed(alive: boolean): number {
    const s = Math.round(BASE_SPEED * PRESETS[this.preset].playerSpeed);
    if (!alive) return Math.round(s * GHOST_SPEED_FACTOR);
    // the mode's speed as the server last said (a power-up boost, the hide-and-seek agbero's head
    // start and final stretch); the next snapshot corrects its end
    const v = this.view;
    return v ? viewSpeed(v, s) : s;
  }

  /** Rewind to the server's position and replay the inputs it hasn't applied yet. */
  private reconcile(view: PlayerView): void {
    const map = this.map;
    const ack = view.you.lastInputSeq;
    this.inputs = this.inputs.filter((i) => i.n > ack);
    let k = { pos: { ...view.you.pos }, vel: { ...view.you.vel } };
    const race = view.race && raceKit();
    const headingBefore = this.predictedHeading;
    if (race && view.race?.foot) {
      // out of the keke on foot with the can: the stick walks the driver, as the server walks them
      let now = view.now;
      for (const i of this.inputs) {
        now += TICK_MS;
        k = stepPlayer(k, i, map, race.viewFoot(view, now) ?? 0, false);
      }
      this.predictedNow = now;
    } else if (race && view.race) {
      // a race keke: heading and speed carry over, and its timers (boost, pit lane, spin) say how
      // it drives at each step, exactly as the server steps it
      let m = { pos: k.pos, heading: view.race.car.heading, spd: view.race.car.spd, vel: k.vel };
      let now = view.now;
      for (const i of this.inputs) {
        now += TICK_MS;
        const d = race.viewDrive(view, map, m.pos, now);
        if (d) m = race.stepKeke(m, { dx: i.dx, dy: i.dy, brake: i.b }, map, d);
      }
      k = { pos: m.pos, vel: m.vel };
      this.predictedHeading = m.heading;
      this.predictedSpd = m.spd;
      this.predictedNow = now;
    } else {
      const speed = this.speed(view.you.alive);
      const brake = viewBrake(view);
      for (const i of this.inputs) k = stepPlayer(k, i, map, speed, !view.you.alive, brake);
    }
    const p = k.pos;
    if (this.predicted) {
      // small corrections snap; bigger ones blend in over ~100 ms so they never look like teleports
      const ex = this.predicted.x + this.visualOffset.x - p.x;
      const ey = this.predicted.y + this.visualOffset.y - p.y;
      this.visualOffset = Math.hypot(ex, ey) < SNAP_UNITS ? { x: 0, y: 0 } : { x: ex, y: ey };
      // the tick being drawn moves with the correction, so the glide between ticks carries on unbroken
      if (this.prevPredicted)
        this.prevPredicted = {
          x: this.prevPredicted.x + p.x - this.predicted.x,
          y: this.prevPredicted.y + p.y - this.predicted.y,
        };
      if (race) this.prevHeading += turn256(this.predictedHeading - headingBefore);
    } else this.prevPredicted = null;
    this.predicted = p;
    this.predictedVel = k.vel;
  }

  /** One local input per 50 ms tick while moving. Returns the frame to send, or null. */
  stepInput(dx: number, dy: number): { n: number; dx: number; dy: number; b?: boolean } | null {
    const v = this.view;
    if (v?.phase !== 'PLAYING' || !this.predicted) return null;
    const race = v.race && raceKit();
    if (race && v.race) {
      // a race keke drives itself: every tick goes, with the stick and Brake as they are
      const input: PendingInput = { n: ++this.inputN, dx, dy, ...(this.brake ? { b: true } : {}) };
      this.lastSent = { dx, dy };
      this.inputs.push(input);
      if (this.inputs.length > 64) this.inputs.shift();
      const map = this.map;
      this.predictedNow = Math.max(this.predictedNow, v.now) + TICK_MS;
      if (v.race.foot) {
        // on foot with the can: the driver walks where the stick points
        const k = stepPlayer(
          { pos: this.predicted, vel: this.predictedVel },
          input,
          map,
          race.viewFoot(v, this.predictedNow) ?? 0,
          false,
        );
        this.predicted = k.pos;
        this.predictedVel = k.vel;
        return input;
      }
      const d = race.viewDrive(v, map, this.predicted, this.predictedNow);
      if (d) {
        this.markStep();
        const m = race.stepKeke(
          { pos: this.predicted, heading: this.predictedHeading, spd: this.predictedSpd },
          { dx, dy, brake: this.brake },
          map,
          d,
        );
        this.predicted = m.pos;
        this.predictedVel = m.vel;
        this.predictedHeading = m.heading;
        this.predictedSpd = m.spd;
      }
      return input;
    }
    if (v.you.vent) return null; // in a shortcut: the keke goes nowhere until it climbs out
    // while still, one zero input says "stopped"; after that, nothing until the stick moves.
    // A keke still braking keeps stepping, so the prediction stops where the server's does.
    const rolling = this.predictedVel.x !== 0 || this.predictedVel.y !== 0;
    if (dx === 0 && dy === 0 && this.lastSent.dx === 0 && this.lastSent.dy === 0 && !rolling) return null;
    this.lastSent = { dx, dy };
    const input = { n: ++this.inputN, dx, dy };
    this.inputs.push(input);
    if (this.inputs.length > 64) this.inputs.shift();
    this.markStep();
    const k = stepPlayer(
      { pos: this.predicted, vel: this.predictedVel },
      input,
      this.map,
      this.speed(v.you.alive),
      !v.you.alive,
      viewBrake(v),
    );
    this.predicted = k.pos;
    this.predictedVel = k.vel;
    return input;
  }

  /**
   * A tick is about to be stepped: the glide starts again from where the keke is drawn right now
   * (not from the last tick), so a tick that comes a little early or late never jerks it.
   */
  private markStep(): void {
    const p = this.predicted;
    if (!p) return;
    const was = this.prevPredicted && this.slides() ? this.glide() : 1;
    const a = this.prevPredicted;
    this.prevPredicted = a ? { x: a.x + (p.x - a.x) * was, y: a.y + (p.y - a.y) * was } : { x: p.x, y: p.y };
    this.prevHeading = this.prevHeading + turn256(this.predictedHeading - this.prevHeading) * (a ? was : 1);
    this.stepAt = performance.now();
  }

  /**
   * How far the drawing has glided toward the newest tick (0 to 1). The glide takes a quarter longer
   * than a tick, so the next tick nearly always lands before it ends: the keke never stands still
   * for a frame between ticks (it trails the prediction by a steady few milliseconds instead).
   */
  private glide(): number {
    return Math.max(0, Math.min(1, (performance.now() - this.stepAt) / (TICK_MS * 1.25)));
  }

  /** A jump (a respawn, a pit's tow) is drawn where it lands, never slid to. */
  private slides(): boolean {
    const p = this.predicted;
    const a = this.prevPredicted;
    return p !== null && a !== null && Math.abs(p.x - a.x) + Math.abs(p.y - a.y) < 400;
  }

  /** Where to draw my keke this frame: between the last two ticks, plus a decaying correction. */
  myDrawPos(dtMs: number): Vec | null {
    if (!this.predicted) return this.view ? this.view.you.pos : null;
    const k = Math.exp(-dtMs / 100);
    this.visualOffset = { x: this.visualOffset.x * k, y: this.visualOffset.y * k };
    const p = this.predicted;
    const a = this.prevPredicted;
    const u = this.slides() ? this.glide() : 1;
    const x = a ? a.x + (p.x - a.x) * u : p.x;
    const y = a ? a.y + (p.y - a.y) * u : p.y;
    return { x: x + this.visualOffset.x, y: y + this.visualOffset.y };
  }

  /** Race: my heading to draw (256ths of a turn, not whole), gliding between ticks like the position. */
  myDrawHeading(): number {
    if (!this.prevPredicted) return this.predictedHeading;
    return this.prevHeading + turn256(this.predictedHeading - this.prevHeading) * this.glide();
  }

  /**
   * Whose look to draw on a keke: the park member who drives that colour (colours are one per
   * member). A copy-copy's disguise wears the copied keke's colour, so it wears that keke's look too.
   */
  lookIdFor(id: string, colour: string): string {
    const owner = this.lobby?.members.find((m) => m.colour === colour)?.id;
    return owner ?? id;
  }

  /** Other kekes, drawn ~120 ms in the past between the two snapshots around that moment. */
  othersAt(now: number): Array<{
    id: string;
    pos: Vec;
    colour: string;
    name: string;
    alive: boolean;
    shadow: boolean;
    shield: boolean;
    /** Race: spinning out, or in the air off a bump (only on a keke in sight; the server says). */
    spin: boolean;
    fly: boolean;
    /** Race: the driver is out on foot with a can (pos is the driver); the keke stands at foot.keke. */
    foot: { keke: Vec; full: boolean; down: boolean } | undefined;
  }> {
    const out: Array<{
      id: string;
      pos: Vec;
      colour: string;
      name: string;
      alive: boolean;
      shadow: boolean;
      shield: boolean;
      spin: boolean;
      fly: boolean;
      foot: { keke: Vec; full: boolean; down: boolean } | undefined;
    }> = [];
    const latest = this.view;
    if (!latest) return out;
    // the moment to draw, on the server's clock (snapshot times, not their jittery arrival), a
    // little in the past so there is nearly always a snapshot either side of it
    const t = (Number.isNaN(this.serverOff) ? latest.now : now + this.serverOff) - INTERP_DELAY_MS;
    let a: Snap | undefined;
    let b: Snap | undefined;
    for (let i = this.snaps.length - 1; i >= 0; i--) {
      const s = this.snaps[i] as Snap;
      if (s.view.now <= t) {
        a = s;
        b = this.snaps[i + 1];
        break;
      }
    }
    // late snapshots: carry on from the last two along their step for a moment, then hold
    let ahead = 0;
    if (a && !b && this.snaps.length > 1) {
      b = a;
      a = this.snaps[this.snaps.length - 2];
      ahead = Math.min(EXTRAPOLATE_MS, t - b.view.now);
    }
    a ??= this.snaps[0];
    for (const p of latest.players) {
      if (!p.pos) continue; // not visible right now: nothing to draw
      const pa = a?.view.players.find((x) => x.id === p.id)?.pos;
      const pb = b?.view.players.find((x) => x.id === p.id)?.pos;
      let pos = p.pos;
      if (pa && pb && a && b && b.view.now > a.view.now) {
        const span = b.view.now - a.view.now;
        const u = ahead > 0 ? 1 + ahead / span : Math.min(1, Math.max(0, (t - a.view.now) / span));
        // a jump between two snapshots (a respawn, a pit's tow) is drawn where it lands
        pos =
          Math.abs(pb.x - pa.x) + Math.abs(pb.y - pa.y) > 400
            ? pb
            : { x: pa.x + (pb.x - pa.x) * u, y: pa.y + (pb.y - pa.y) * u };
      } else if (pa) {
        pos = pa;
      }
      out.push({
        id: p.id,
        pos,
        colour: p.colour,
        name: p.name,
        alive: p.alive,
        shadow: p.shadow === true,
        shield: p.shield === true,
        spin: p.spin === true,
        fly: p.fly === true,
        foot: p.foot,
      });
    }
    return out;
  }

  /** Vision radius in world units for the fog (decoration only; the server already filtered). */
  visionRadius(): number | null {
    const v = this.view;
    if (!v?.you.alive || v.phase !== 'PLAYING') return null;
    // a race sees the whole ring (the view sends every keke): no darkness round the keke, only a NEPA's
    // headlamp beam while it lasts. The road's own night and haze still show how far the eye carries.
    if (v.race && !(v.race.car.nepaUntil > v.now)) return null;
    // the server's own radius for me, when it sent one (every worker since FE-15 does)
    if (typeof v.you.vision === 'number' && v.you.vision > 0) return v.you.vision;
    const c = PRESETS[this.preset];
    const base = (v.you.role === 'impostor' ? c.impostorVision : c.crewVision) * TILE;
    // the wiper clears the darkness around you; NEPA leaves a driver a headlamp beam
    if (v.you.power && v.you.power.wiperUntil > v.now) return Math.round(base * WIPER_FACTOR);
    if (v.sabotage?.kind === 'nepa' && v.you.role === 'crew') return Math.round(base * c.lightsOutFactor);
    // hide and seek: the agbero sees further in the final stretch
    const h = v.hide;
    if (h && v.you.role === 'impostor' && v.now >= h.finalAt) return Math.round(base * (c.hide?.finalVision ?? 1));
    return Math.round(base);
  }

  private skyFrom: Sky = 'day@day';
  private skyTo: Sky = 'day@day';
  private skyAt = -Infinity;
  /** The blend under way is a change of the time of day (the long one), not a weather turn. */
  private skyDusk = false;

  /**
   * The weather to draw, as a blend (looks only; the rules follow the view's own weather): `from`
   * fading into `to` by `mix`. A turn starts blending with its warning (clouds gather, dust lifts)
   * and is whole once it lands; a turn into or out of night is a 20-second dusk or dawn. Before a
   * round, the park's bought weather or a plain day.
   */
  sky(now: number): { from: Sky; to: Sky; mix: number } {
    const v = this.view;
    const w = v?.weather;
    const theme = this.lobby?.theme as SkyWeather | undefined;
    const weather: SkyWeather = w?.turn?.to ?? w?.kind ?? theme ?? 'day';
    // in a round the clock says (it starts in daylight); with no round, daylight
    const time: SkyTime = v?.daylight?.time ?? 'day';
    const target = (weather === 'night' ? 'day@night' : `${weather}@${time}`) as Sky;
    if (target !== this.skyTo) {
      // a new round's sky is simply there; on the road a weather turn blends over its warning and
      // the clock moving on (day to evening, evening to night) over a dusk
      const [wasWeather, wasTime] = skyParts(this.skyTo);
      const [toWeather, toTime] = skyParts(target);
      const live = v?.phase === 'PLAYING';
      const dusk = live && HOURS.indexOf(toTime) > HOURS.indexOf(wasTime);
      const turn = live && w?.turn?.to === toWeather && toWeather !== wasWeather;
      const blend = (dusk || turn) && (toWeather === wasWeather || turn) && (toTime === wasTime || dusk);
      this.skyDusk = dusk;
      this.skyFrom = blend ? this.skyTo : target;
      this.skyTo = target;
      this.skyAt = blend ? now : -Infinity;
    }
    const mix = Math.max(0, Math.min(1, (now - this.skyAt) / (this.skyDusk ? DUSK_MS : TURN_MS)));
    return { from: this.skyFrom, to: this.skyTo, mix };
  }

  /** NEPA has my lights off: a living driver without a wiper sees only a headlamp beam. Looks only. */
  lightsOut(): boolean {
    const v = this.view;
    if (!v?.you.alive || v.phase !== 'PLAYING' || v.you.role !== 'crew') return false;
    if (v.you.power && v.you.power.wiperUntil > v.now) return false;
    return v.sabotage?.kind === 'nepa';
  }
}

export const TICK = TICK_MS;

/** A change of race heading (256ths of a turn) folded to the shorter way round: -128 to 128. */
function turn256(d: number): number {
  return d - 256 * Math.round(d / 256);
}
